import { defaultColumnWidths } from "@/lib/interfaces and Enums/ENUMColumnWidths";
import { ExecutionRequestStatus } from "@/lib/interfaces and Enums/ENUMExecutionRequestStatus";
import { InstrumentTypeCode } from "@/lib/interfaces and Enums/ENUMInstrumentTypeCode";
import { SecurityTypeCode } from "@/lib/interfaces and Enums/ENUMSecurityTypeCode";
import { StrategyType } from "@/lib/interfaces and Enums/ENUMStrategyType";
import { StructureTypeCode } from "@/lib/interfaces and Enums/ENUMStructureTypeCode";
import { TableHeaderField } from "@/lib/interfaces and Enums/ENUMTableHeaders";
import { fillerColDef } from "@/lib/fillerColDef";
import {
  returnPercentageFormatted,
  returnSizeFormatted,
} from "@/lib/globalFormatterHelper";
import tradeDataStore from "@/store/tradeDataStore";
import { updateQuotesAcrossTables } from "@/store/tradeDataStoreACTIONS";
import { ColDef } from "ag-grid-community";
import {
  caluclatePercentageQuoteSizeER,
  isCPIEditable,
  returnColDefsAsNewArray,
  tradeHasBondOrBondOption,
} from "../actionSummaryColDefHelperFunctions";
import { applyColumnWidthMultiplier } from "../actionSummaryHelper";
import { IColDefsObject } from "../actionSummaryMainTableColDefs";
import CashCellEditor from "../cellEditors/CashCellEditor";
import CPICellEditor from "../cellEditors/CPICellEditor";
import DynamicCellEditorQuoteDollarFraction from "../cellEditors/DynamicCellEditorQuoteDollarFraction";
import DynamicCellEditorQuoteSize from "../cellEditors/DynamicCellEditorQuoteSize";
import DynamicCellEditorQuoteSizeExecutionRequest from "../cellEditors/DynamicCellEditorQuoteSizeExecutionRequest";
import BookCellRenderer from "../cellRenderers/BookCellRenderer";
import CashCellRenderer from "../cellRenderers/CashCellRenderer";
import CouponActionSummarySwapCellRenderer from "../cellRenderers/CouponActionSummarySwapCellRenderer";
import { couponCellValueFormatter } from "../cellRenderers/CouponActionSummarySwapCellRenderer/couponActionSummarySwapFormatter";
import CPICellRenderer from "../cellRenderers/CPICellRenderer";
import CpiIndexCellRenderer from "../cellRenderers/CpiIndexCellRenderer";
import DealerCellRenderer from "../cellRenderers/DealerCellRenderer";
import { dealerCellValueFormatter } from "../cellRenderers/dealerCellFormatter";
import { cashCellValueFormatter } from "../cellRenderers/cashCellFormatter";
import { dealtFeeCellValueFormatter } from "../cellRenderers/dealtFeeCellFormatter";
import DealtFeeCellRenderer from "../cellRenderers/DealtFeeCellRenderer";
import DealtPriceCellRenderer from "../cellRenderers/DealtPriceCellRenderer";
import { dealtPriceValueFormatter } from "../cellRenderers/DealtPriceCellRenderer/dealtPriceFormatter";
import DealtPriceHeader from "../cellRenderers/DealtPriceHeader";
import {
  CurrentCouponCellRenderer,
  QuotedMarginCellRenderer,
  ReferenceIndexCellRenderer,
} from "../cellRenderers/FRNBlockCellRenderers";
import {
  currentCouponValueFormatter,
  quotedMarginValueFormatter,
  referenceIndexValueFormatter,
} from "../cellRenderers/FRNBlockCellRenderers/frnBlockFormatter";
import DirectionCellRenderer from "../cellRenderers/DirectionCellRenderer/index";
import { directionValueFormatter } from "../cellRenderers/DirectionCellRenderer/directionFormatter";
import ExecutionFeeCellRenderer from "../cellRenderers/ExecutionFeeCellRenderer";
import ExpiryDateCellRenderer from "../cellRenderers/ExpiryDateCellRenderer";
import LevelCellRenderer from "../cellRenderers/LevelCellRenderer";
import { levelValueFormatter } from "../cellRenderers/LevelCellRenderer/levelFormatter";
import NotionalLeg1 from "../cellRenderers/NotionalLeg1";
import { notionalLeg1ValueFormatter } from "../cellRenderers/NotionalLeg1/notionalLeg1Formatter";
import QuotedPercentCellRenderer from "../cellRenderers/QuotedPercentCellRenderer";
import { quotedPercentValueFormatter } from "../cellRenderers/QuotedPercentCellRenderer/quotedPercentFormatter";
import QuoteCellRenderer from "../cellRenderers/QuoteCellRenderer";
import SettlementDateCellRenderer from "../cellRenderers/SettlementDateCellRenderer";
import { settlementDateValueFormatter } from "../cellRenderers/SettlementDateCellRenderer/settlementDateFormatter";
import StrikeCellRenderer from "../cellRenderers/StrikeCellRenderer";
import TradeRootStringCellRenderer from "../cellRenderers/TradeRootStringCellRenderer";
import { tradeRootStringValueFormatter } from "../cellRenderers/TradeRootStringCellRenderer/tradeRootStringFormatter";
import CoverCellRenderer from "../cellRenderers/CoverCellRenderer";
import {
  coverCellValueFormatter,
  hasCoverValue,
} from "../cellRenderers/CoverCellRenderer/coverFormatter";
import UnwindTypeCellRenderer from "../cellRenderers/UnwindTypeCellRenderer";
import { returnTrueIfCellCanBeEdited } from "../columnHelpers/cellCanBeEdited";
import { ColId } from "../ENUMActionSummaryColIds";
import { displayYieldTooltipForAllQuoteLevels } from "../tooltips/tooltipHelperFunctions";
import {
  quoteValueFormatter,
  statusAtAffirmedRange,
  statusAtExecutedRange,
  statusFormExecutedToAffirmedRange,
  statusFromAwarded,
  statusFromReceivedToQuoted,
  statusToShowDealtPrice,
  // LQC-275: % Quoted column disabled on RP ER until RP-side behaviour is finalised
  // tradeHasReducedQuotedSize,
} from "./colDefsShared";
import CashDealerTooltip from "../tooltips/CashDealerTooltip";
import { feeAdjustedYieldTooltipValueGetter } from "../tooltips/FeeAdjustedYieldTooltip";

// LQC-2407: the cover/coverSpread/competition ColIds are needed by every RP ER
// branch (Outright, structures, SYN REPO). Kept in one place so a branch can't
// silently miss them again - SYN REPO did, so the runtime toggle in
// handleCoverColumnVisibility showed them and every colDef rebuild hid them.
export function coverColIdsWhenAdvised(fullTradeData: any): ColId[] {
  return [
    ...(hasCoverValue(fullTradeData?.cover) ? [ColId.cover] : []),
    ...(hasCoverValue(fullTradeData?.coverSpread) ? [ColId.coverSpread] : []),
    ...(hasCoverValue(fullTradeData?.competition) ? [ColId.competition] : []),
  ];
}

export function returnExecutionRequestColDefsRP({
  theme,
  fullTradeData,
  columnWidthMultiplier = 1.0,
}: IColDefsObject) {
  const executionRequestColDefsRP = [
    {
      field: "referenceSpread",
      headerName: TableHeaderField.level,
      colId: ColId.level,
      cellRendererParams: { fullTradeData: fullTradeData },
      cellRenderer: LevelCellRenderer,
      valueFormatter: levelValueFormatter,
      width: defaultColumnWidths.level,
    },
    // {
    //   field: "filledPercent",
    //   headerName: TableHeaderField.filledPercent,
    //   colId: ColId.filledPercent,
    //   width: defaultColumnWidths.filledPercent,
    // },
    {
      field: "bid",
      tooltipValueGetter: displayYieldTooltipForAllQuoteLevels,
      headerName: TableHeaderField.bid,
      colId: ColId.bidOutright,
      editable: (params: any) =>
        returnTrueIfCellCanBeEdited(params.data.status, ColId.bidOutright),
      cellEditor: DynamicCellEditorQuoteDollarFraction,
      width: defaultColumnWidths.quoteField,
      cellClass: "ag-roundedInputStyle",
      valueSetter: (params: any) => {
        params.data.bid = params.newValue;
        if (params.data?.legs?.[0]) {
          params.data.legs[0].bid = params.newValue;
        }
        updateQuotesAcrossTables(params);
        return false;
      },
      valueGetter: (params: any) => {
        return params.data?.legs?.[0]?.bid;
      },
      cellRendererParams: { fullTradeData: fullTradeData, tableLevel: "ER" },
      cellRenderer: QuoteCellRenderer,
      valueFormatter: quoteValueFormatter,
    },
    {
      field: "bid",
      tooltipValueGetter: displayYieldTooltipForAllQuoteLevels,
      headerName: TableHeaderField.bid,
      colId: ColId.bid,
      cellEditor: DynamicCellEditorQuoteDollarFraction,
      editable: (params: any) =>
        returnTrueIfCellCanBeEdited(params.data.status, ColId.bid),
      valueSetter: (params: any) => {
        params.data.bid = params.newValue;
        updateQuotesAcrossTables(params);
        return false;
      },
      width: defaultColumnWidths.quoteField,
      cellClass: "ag-roundedInputStyle",
      cellRendererParams: { fullTradeData: fullTradeData, tableLevel: "ER" },
      cellRenderer: QuoteCellRenderer,
      valueFormatter: quoteValueFormatter,
    },
    {
      field: "bid",
      tooltipValueGetter: displayYieldTooltipForAllQuoteLevels,
      headerName: TableHeaderField.bidRepo,
      colId: ColId.bidRepo,
      cellEditor: DynamicCellEditorQuoteDollarFraction,
      editable: (params: any) =>
        returnTrueIfCellCanBeEdited(params.data.status, ColId.bidRepo),
      valueSetter: (params: any) => {
        params.data.bid = params.newValue;
        updateQuotesAcrossTables(params);
        return false;
      },
      width: defaultColumnWidths.quoteField,
      cellClass: "ag-roundedInputStyle",
      cellRendererParams: { fullTradeData: fullTradeData, tableLevel: "ER" },
      cellRenderer: QuoteCellRenderer,
      valueFormatter: quoteValueFormatter,
    },
    {
      field: "ask",
      tooltipValueGetter: displayYieldTooltipForAllQuoteLevels,
      headerName: TableHeaderField.ask,
      colId: ColId.askOutright,
      editable: (params: any) =>
        returnTrueIfCellCanBeEdited(params.data.status, ColId.askOutright),
      cellEditor: DynamicCellEditorQuoteDollarFraction,
      cellClass: "ag-roundedInputStyle",
      width: defaultColumnWidths.quoteField,
      valueGetter: (params: any) => params.data?.legs?.[0]?.ask,
      valueSetter: (params: any) => {
        params.data.ask = params.newValue;
        if (params.data?.legs?.[0]) {
          params.data.legs[0].ask = params.newValue;
        }
        updateQuotesAcrossTables(params);
        return false;
      },
      cellRendererParams: { fullTradeData: fullTradeData, tableLevel: "ER" },
      cellRenderer: QuoteCellRenderer,
      valueFormatter: quoteValueFormatter,
    },
    {
      field: "ask",
      tooltipValueGetter: displayYieldTooltipForAllQuoteLevels,
      headerName: TableHeaderField.ask,
      colId: ColId.ask,
      cellEditor: DynamicCellEditorQuoteDollarFraction,
      editable: (params: any) =>
        returnTrueIfCellCanBeEdited(params.data.status, ColId.ask),
      valueSetter: (params: any) => {
        params.data.ask = params.newValue;
        updateQuotesAcrossTables(params);
        return false;
      },
      width: defaultColumnWidths.quoteField,
      cellClass: "ag-roundedInputStyle",
      cellRendererParams: { fullTradeData: fullTradeData, tableLevel: "ER" },
      cellRenderer: QuoteCellRenderer,
      valueFormatter: quoteValueFormatter,
    },
    {
      field: "ask",
      tooltipValueGetter: displayYieldTooltipForAllQuoteLevels,
      headerName: TableHeaderField.askRepo,
      colId: ColId.askRepo,
      cellEditor: DynamicCellEditorQuoteDollarFraction,
      editable: (params: any) =>
        returnTrueIfCellCanBeEdited(params.data.status, ColId.askRepo),
      valueSetter: (params: any) => {
        params.data.ask = params.newValue;
        updateQuotesAcrossTables(params);
        return false;
      },
      width: defaultColumnWidths.quoteField,
      cellClass: "ag-roundedInputStyle",
      cellRendererParams: { fullTradeData: fullTradeData, tableLevel: "ER" },
      cellRenderer: QuoteCellRenderer,
      valueFormatter: quoteValueFormatter,
    },
    {
      field: "mid",
      tooltipValueGetter: displayYieldTooltipForAllQuoteLevels,
      headerName: TableHeaderField.mid,
      colId: ColId.midOutright,
      cellEditor: DynamicCellEditorQuoteDollarFraction,
      width: defaultColumnWidths.quoteField,
      cellClass: "ag-roundedInputStyle",
      cellRendererParams: { fullTradeData: fullTradeData, tableLevel: "ER" },
      cellRenderer: QuoteCellRenderer,
      valueGetter: (params: any) => params.data?.legs?.[0]?.mid,
      valueSetter: (params: any) => {
        params.data.mid = params.newValue;
        if (params.data?.legs?.[0]) {
          params.data.legs[0].mid = params.newValue;
        }
        updateQuotesAcrossTables(params);
        return false;
      },
      valueFormatter: quoteValueFormatter,
    },
    {
      field: "mid",
      tooltipValueGetter: displayYieldTooltipForAllQuoteLevels,
      headerName: TableHeaderField.mid,
      colId: ColId.mid,
      cellEditor: DynamicCellEditorQuoteDollarFraction,
      valueSetter: (params: any) => {
        params.data.mid = params.newValue;
        updateQuotesAcrossTables(params);
        return false;
      },
      width: defaultColumnWidths.quoteField,
      cellClass: "ag-roundedInputStyle",
      cellRendererParams: { fullTradeData: fullTradeData, tableLevel: "ER" },
      cellRenderer: QuoteCellRenderer,
      valueFormatter: quoteValueFormatter,
    },
    {
      field: "mid",
      tooltipValueGetter: displayYieldTooltipForAllQuoteLevels,
      headerName: TableHeaderField.midRepo,
      colId: ColId.midRepo,
      cellEditor: DynamicCellEditorQuoteDollarFraction,
      valueSetter: (params: any) => {
        params.data.mid = params.newValue;
        updateQuotesAcrossTables(params);
        return false;
      },
      width: defaultColumnWidths.quoteField,
      cellClass: "ag-roundedInputStyle",
      cellRendererParams: { fullTradeData: fullTradeData, tableLevel: "ER" },
      cellRenderer: QuoteCellRenderer,
      valueFormatter: quoteValueFormatter,
    },
    {
      field: "feeAdjustedQuote",
      headerComponent: DealtPriceHeader, //TableHeaderField.feeAdjustedQuote normally - TableHeaderField.dealtRepoRate if SYN repo.
      headerComponentParams: { fullTradeData: fullTradeData },
      colId: ColId.feeAdjustedQuote,
      width: defaultColumnWidths.feeAdjustedQuote,
      cellRenderer: DealtPriceCellRenderer,
      cellRendererParams: { fullTradeData: fullTradeData },
      valueFormatter: dealtPriceValueFormatter,
      tooltipValueGetter: feeAdjustedYieldTooltipValueGetter
      
    },
    // {
    //   // TODO: Used for NPV Outrights?
    //   field: "feeAdjustedQuote",
    //   headerComponent: DealtPriceHeader, //TableHeaderField.feeAdjustedQuote normally - TableHeaderField.dealtRepoRate if SYN repo.
    //   headerComponentParams: { fullTradeData: fullTradeData },
    //   colId: ColId.feeAdjustedQuoteOutright,
    //   width: defaultColumnWidths.feeAdjustedQuote,
    //   cellRenderer: DealtPriceCellRenderer,
    //   cellRendererParams: { fullTradeData: fullTradeData },
    // },
    {
      field: "strike",
      headerName: TableHeaderField.strike,
      colId: ColId.strikeOutright,
      width: defaultColumnWidths.strike,
      cellStyle: { padding: "0px" },
      cellRendererParams: { fullTradeData: fullTradeData },
      cellRenderer: StrikeCellRenderer,
    },
    {
      field: "unwind",
      headerName: TableHeaderField.unwind,
      extraLargeScaling: true,
      colId: ColId.unwind,
      width: defaultColumnWidths.unwind,
      cellRenderer: UnwindTypeCellRenderer,
      valueFormatter: (params: any) => params.value,
      cellRendererParams: { fullTradeData: fullTradeData },
    },
    {
      field: "dealer",
      headerName: TableHeaderField.dealer,
      colId: ColId.dealer,
      width: defaultColumnWidths.dealer,
      headerClass: "noRightBorder",
      cellRenderer: DealerCellRenderer,
      valueFormatter: dealerCellValueFormatter,
      cellRendererParams: { fullTradeData: fullTradeData, theme: theme },
      tooltipValueGetter: (params: any) => (params.data ? "dealer" : null),
      tooltipComponent: CashDealerTooltip,
    },
    {
      field: "dealtFee", //npv Cash(fee inc),
      headerName: TableHeaderField.dealtFee,
      colId: ColId.dealtFeeOutright, // Outrigtht.
      width: defaultColumnWidths.dealtFee,
      cellRendererParams: { fullTradeData: fullTradeData },
      cellRenderer: DealtFeeCellRenderer,
      valueFormatter: dealtFeeCellValueFormatter,
      tooltipValueGetter: (params: any) => (params.data ? "dealtFee" : null),
      tooltipComponent: CashDealerTooltip,
    },
    {
      field: "cash",
      headerName: TableHeaderField.cash,
      colId: ColId.cash,
      width: defaultColumnWidths.cash,
      editable: (_params: any) => {
        const currentTradeData = tradeDataStore.getState().tradeData;
        const thisTrade = currentTradeData.find(
          (trade) => trade.esid == fullTradeData.esid,
        );
        if (thisTrade?.status == ExecutionRequestStatus.DealtAway) {
          return false;
        }
        if (thisTrade?.status && statusAtExecutedRange.includes(thisTrade.status)) {
          return false;
        }
        // Update the editable criteria for outrights.
        if (
          fullTradeData.unwind == true ||
          (fullTradeData.unwind == false &&
            fullTradeData.legs?.length == 1 &&
            fullTradeData.legs[0].unwind == "NPV") ||
          (fullTradeData.unwind == false &&
            fullTradeData.legs?.length == 1 &&
            fullTradeData.legs[0].unwind == "Recoupon")
        ) {
          return true;
        } else {
          return false;
        }
      },
      cellEditor: CashCellEditor,
      cellRenderer: CashCellRenderer,
      valueFormatter: cashCellValueFormatter,
      cellRendererParams: { fullTradeData: fullTradeData },
      tooltipValueGetter: (params: any) => (params.data ? "cash" : null),
      tooltipComponent: CashDealerTooltip,
    },
    // OUTRIGHT FEE
    {
      field: "executionFee", // NPV as Fee
      headerName: TableHeaderField.executionFee,
      colId: ColId.executionFeeOutright,
      width: defaultColumnWidths.executionFee,
      cellRendererParams: { fullTradeData: fullTradeData },
      cellRenderer: ExecutionFeeCellRenderer,
    },
    {
      field: "direction",
      headerName: TableHeaderField.direction,
      colId: ColId.direction,
      width: defaultColumnWidths.side,
      cellRendererParams: { fullTradeData: fullTradeData },
      cellRenderer: DirectionCellRenderer,
      valueFormatter: directionValueFormatter,
    },
    {
      field: "coupon", // OUTRIGHT - 14614
      headerName: TableHeaderField.coupon,
      colId: ColId.couponOutright,
      width: defaultColumnWidths.coupon,
      cellRenderer: CouponActionSummarySwapCellRenderer,
      valueFormatter: couponCellValueFormatter,
      cellRendererParams: { fullTradeData: fullTradeData },
    },
    //15051 - added size for RP Outrights
    {
      field: "notionalLeg1",
      headerName: TableHeaderField.notionalLeg1,
      colId: ColId.notionalLeg1,
      width: defaultColumnWidths.nominal,
      cellRendererParams: { fullTradeData: fullTradeData },
      cellRenderer: NotionalLeg1,
      valueFormatter: notionalLeg1ValueFormatter,
    },
    {
      // OUTRIGHT
      field: "quotedSize",
      headerName: TableHeaderField.quotedSize,
      colId: ColId.quotedSizeOutright,
      cellRendererParams: { fullTradeData: fullTradeData },
      cellEditor: DynamicCellEditorQuoteSize,
      editable: (params: any) => {
        return returnTrueIfCellCanBeEdited(params.data.status, ColId.quotedSizeOutright, undefined, params.data.structureType as StructureTypeCode, fullTradeData, params);
      },
      cellClass: (params: any) => {
        return returnTrueIfCellCanBeEdited(params.data.status, ColId.quotedSizeOutright, undefined, params.data.structureType as StructureTypeCode, fullTradeData, params) == true
          ? theme == "light"
            ? "inputCellOtherLight roundedInputCell"
            : "inputCellOtherDark roundedInputCell"
          : "roundedInputCell";
      },
      valueGetter: (params: any) => params.data?.legs?.[0]?.quotedSize,
      valueFormatter: (params: any) => {
        return params.value
          ? returnSizeFormatted(
              params.value,
              params.data?.legs?.[0]?.instrumentType,
            )
          : "";
      },
      cellDataType: "number",
      width: defaultColumnWidths.quotedSize,
    },
    {
      // OUTRIGHT — bar version of % quoted.
      // hide: true so unselected paths default to hidden;
      // selection arrays opt in via ColId.percentQuotedOutright when re-enabled.
      field: "quotedSize",
      headerName: TableHeaderField.percentQuoted,
      colId: ColId.percentQuotedOutright,
      width: defaultColumnWidths.percentQuoted,
      cellRendererParams: { fullTradeData: fullTradeData },
      cellRenderer: QuotedPercentCellRenderer,
      valueFormatter: quotedPercentValueFormatter,
      hide: true,
    },
    // -- Used for structures
    // -- If a user enters a % in the filed, it will update the blocks as a percentage of their nominal to that amount.
    {
      field: "quotedSize",
      headerName: TableHeaderField.quotedSize,
      colId: ColId.quotedSize,
      cellRendererParams: { fullTradeData: fullTradeData },
      cellEditor: DynamicCellEditorQuoteSizeExecutionRequest,
      editable: (params: any) =>
        returnTrueIfCellCanBeEdited(params.data.status, ColId.quotedSize, undefined, params.data.structureType as StructureTypeCode, fullTradeData, params),
      cellClass: (params: any) => {
        return returnTrueIfCellCanBeEdited(params.data.status,  ColId.quotedSize, undefined, params.data.structureType as StructureTypeCode, fullTradeData, params) == true
          ? theme == "light"
            ? "inputCellOtherLight roundedInputCell"
            : "inputCellOtherDark roundedInputCell"
          : "roundedInputCell";
      },
      valueGetter: (params: any) => caluclatePercentageQuoteSizeER(params),
      cellDataType: "number",
      width: defaultColumnWidths.quotedSize,
      valueFormatter: (params: any) => {
        return params.value
          ? `${returnPercentageFormatted(params.value)}%`
          : "";
      },
    },
    {
      // STRUCTURE — bar version of % quoted (aggregate across legs).
      // hide: true so unselected paths default to hidden;
      // selection arrays opt in via ColId.percentQuoted when re-enabled.
      field: "quotedSize",
      headerName: TableHeaderField.percentQuoted,
      colId: ColId.percentQuoted,
      width: defaultColumnWidths.percentQuoted,
      cellRendererParams: { fullTradeData: fullTradeData },
      cellRenderer: QuotedPercentCellRenderer,
      valueFormatter: quotedPercentValueFormatter,
      hide: true,
    },
    {
      field: "security",
      headerName: TableHeaderField.security,
      colId: ColId.securityOutright,
      width: defaultColumnWidths.name,
      // flex: 2,
      valueGetter: (params: any) => params.data?.legs?.[0]?.securityName,
      tooltipValueGetter: (params: any) => {
        return params.data?.legs?.[0]?.isin;
      },
    },
    // LQC-2153: FRN columns sit after Security - visibility controlled by tempColDefs (Outright RFQ)
    {
      field: "referenceIndex",
      headerName: TableHeaderField.referenceIndex,
      colId: ColId.referenceIndexOutright,
      width: defaultColumnWidths.referenceIndex,
      cellRenderer: ReferenceIndexCellRenderer,
      cellRendererParams: { fullTradeData: fullTradeData },
      valueGetter: (params: any) => params.data?.legs?.[0]?.referenceIndex,
      valueFormatter: referenceIndexValueFormatter,
    },
    {
      field: "quotedMargin",
      headerName: TableHeaderField.quotedMargin,
      colId: ColId.quotedMarginOutright,
      width: defaultColumnWidths.quotedMargin,
      cellRenderer: QuotedMarginCellRenderer,
      cellRendererParams: { fullTradeData: fullTradeData },
      valueGetter: (params: any) => params.data?.legs?.[0]?.quotedMargin,
      valueFormatter: quotedMarginValueFormatter,
    },
    {
      field: "coupon",
      headerName: TableHeaderField.currentCoupon,
      colId: ColId.currentCouponOutright,
      width: defaultColumnWidths.currentCoupon,
      cellRenderer: CurrentCouponCellRenderer,
      cellRendererParams: { fullTradeData: fullTradeData },
      valueGetter: (params: any) => params.data?.legs?.[0]?.coupon,
      valueFormatter: currentCouponValueFormatter,
    },
    // LQC-227: ISIN column for bond trades — visibility controlled by selection arrays
    {
      field: "isin",
      headerName: "ISIN",
      colId: ColId.isinOutright,
      width: 150,
      valueGetter: (params: any) => params.data?.legs?.[0]?.isin,
    },
    {
      field: "settlementDate",
      headerName: TableHeaderField.settlementDate,
      colId: ColId.settlementDateOutright,
      extraScaling: true,
      width: defaultColumnWidths.date,
      valueGetter: (params: any) => params.data?.legs?.[0]?.settlementDate,
      valueSetter: (params: any) => {
        if (params.data?.legs?.[0]) {
          params.data.legs[0].settlementDate = params.newValue;
        }
        return false;
      },
      cellRenderer: SettlementDateCellRenderer,
      cellStyle: { padding: "0px" },
      cellClass: "ag-roundedInputStyle",
      cellRendererParams: { fullTradeData },
      valueFormatter: settlementDateValueFormatter,
      cellDataType: "dateString",
    },
    {
      field: "expiryDate",
      colId: ColId.expiryDate,
      headerName: TableHeaderField.expiryDate,
      width: defaultColumnWidths.date,
      cellRenderer: ExpiryDateCellRenderer,
      cellRendererParams: { fullTradeData: fullTradeData },
    },
    {
      field: "cpi",
      headerName: TableHeaderField.cpiLeg1,
      colId: ColId.cpiLeg1Outright,
      width: defaultColumnWidths.cpi,
      editable: () => isCPIEditable({ esid: fullTradeData.esid }),
      cellStyle: { padding: "0px" },
      cellClass: "ag-roundedInputStyle",
      cellRenderer: CPICellRenderer,
      cellEditor: CPICellEditor,
      cellRendererParams: { fullTradeData: fullTradeData },
      cellDataType: "number",
    },
    {
      field: "cpiIndex",
      headerName: TableHeaderField.cpiIndex,
      colId: ColId.cpiIndexOutright, // OUTRIGHT
      width: defaultColumnWidths.cpiIndex,
      cellRendererParams: { fullTradeData: fullTradeData },
      cellRenderer: CpiIndexCellRenderer,
    },
    {
      field: "notionalLeg2",
      headerName: TableHeaderField.notionalLeg2,
      colId: ColId.notionalLeg2Outright,
      width: defaultColumnWidths.nominal,
      valueGetter: (params: any) => params.data?.legs?.[0]?.notionalLeg2, // brought up to ER in case of outright
      valueFormatter: (params: any) => {
        return params.value
          ? returnSizeFormatted(
              params.value,
              params.data?.legs?.[0]?.instrumentType,
            )
          : "";
      },
    },
    {
      field: "book",
      headerName: TableHeaderField.book,
      colId: ColId.book,
      width: defaultColumnWidths.book,
      cellRenderer: BookCellRenderer,
      cellRendererParams: { fullTradeData: fullTradeData },
    },
    // RP-side trade-root fields populated by the backend when the OP advised
    // the corresponding setting. Hidden by default; the conditional spreads in
    // the selection arrays below flip `hide` to false when the field is
    // present on the trade. (Mirrors the `percentQuotedOutright` pattern.)
    {
      field: "cover",
      headerName: "Cover",
      colId: ColId.cover,
      width: defaultColumnWidths.quotedSize,
      hide: true,
      cellRenderer: CoverCellRenderer,
      cellRendererParams: { fieldName: "cover" },
      valueFormatter: coverCellValueFormatter,
    },
    {
      field: "coverSpread",
      headerName: "Cover Spread",
      colId: ColId.coverSpread,
      width: defaultColumnWidths.quotedSize,
      hide: true,
      cellRenderer: CoverCellRenderer,
      cellRendererParams: { fieldName: "coverSpread" },
      valueFormatter: coverCellValueFormatter,
    },
    {
      field: "competition",
      headerName: "Comp",
      colId: ColId.competition,
      width: defaultColumnWidths.quotedSize,
      hide: true,
      cellRenderer: TradeRootStringCellRenderer,
      cellRendererParams: { fieldName: "competition" },
      valueFormatter: tradeRootStringValueFormatter,
    },
    ...fillerColDef,
  ];
  const structureType = fullTradeData.structureType;
  const strategyType = fullTradeData.strategyType;
  const status = fullTradeData.status;

  const unwind = fullTradeData.unwind;
  let containsRecoupon = false;
  let containsNPV = false;
  if (fullTradeData && fullTradeData.legs && fullTradeData.legs.length > 0) {
    if (fullTradeData.legs.length > 0) {
      fullTradeData.legs.forEach((leg: any) => {
        if (leg.unwind == "Recoupon") {
          containsRecoupon = true;
        } else if (leg.unwind == "NPV") {
          containsNPV = true;
        }
      });
    }
  }

  let colDefsToReturn: ColDef | any = [];
  const columnsToShowForNPVorRecoupon: ColId[] = []; // USED FOR NPV / Recoupon to prevent cols later getting added.
  const columnsToHideForNPVorRecoupon: ColId[] = []; // USED FOR NPV / Recoupon to prevent cols later getting added.

  const dealtPriceCols: any[] = [];
  if (statusToShowDealtPrice.includes(status)) {
    dealtPriceCols.push(ColId.feeAdjustedQuote);
  }
  const bookCols: any[] = [];
  if (statusFromAwarded.includes(status)) {
    bookCols.push(ColId.book);
  }

  const type = fullTradeData.type;

  if (type == "RFQ") {
    if (strategyType == StrategyType.Outright && unwind == false) {
      if (containsNPV) {
        columnsToShowForNPVorRecoupon.push(ColId.unwind);
      } else if (
        containsRecoupon &&
        statusFromAwarded.includes(fullTradeData.status)
      ) {
        columnsToShowForNPVorRecoupon.push(ColId.unwind);
      }
    }

    // if (structureType == StructureTypeCode.NPVPACK) {
    if (unwind == true) {
      // HIDE for all NPV STYLE = TRUE
      // 14176 and 14191
      columnsToHideForNPVorRecoupon.push(
        ColId.bid,
        ColId.bidOutright,
        ColId.ask,
        ColId.askOutright,
        ColId.mid,
        ColId.midOutright,
        // May need to add the Dealt Price back in - Q pending with Jonty.
        ColId.feeAdjustedQuote,
      );

      if (
        statusFromReceivedToQuoted.includes(status) ||
        status == ExecutionRequestStatus.DealtAway // Dealt away same as quoted.
      ) {
        //14176 - SHOW NPV STYLE
        columnsToShowForNPVorRecoupon.push(ColId.dealer, ColId.cash);
      } else if (
        status == ExecutionRequestStatus.Awarded ||
        statusAtExecutedRange.includes(status) ||
        statusAtAffirmedRange.includes(status)
      ) {
        // 14191 (Same as 14235 & 14242) - SHOW NPV Style
        columnsToShowForNPVorRecoupon.push(ColId.dealer, ColId.cash);
        // 14191 (Same as 14235 & 14242) - HIDE NPV Style
      }
    } else if (
      unwind == false &&
      containsNPV &&
      strategyType == StrategyType.Outright
    ) {
      if (!statusFormExecutedToAffirmedRange.includes(status)) {
        // 14573 - RP of an outright block flagged for NPV between status “Received” to “Awarded”.
        columnsToShowForNPVorRecoupon.push(
          ColId.dealer,
          ColId.cash,
          ColId.couponOutright,
        );
      }
      if (statusAtExecutedRange.includes(status)) {
        // 14577 - RP of an outright block flagged for NPV 'Executed'
        columnsToShowForNPVorRecoupon.push(
          ColId.dealer,
          ColId.cash,
          ColId.executionFeeOutright, // Fee
          ColId.feeAdjustedQuote,
          ColId.couponOutright,
        );
        // 14577 - RP of an outright block flagged for NPV 'Executed' - Remove Dealt Price
        // columnsToHideForNPVorRecoupon.push(ColId.feeAdjustedQuote);
      } else if (statusAtAffirmedRange.includes(status)) {
        // 14581 - RP of an outright block flagged for NPV 'Executed' - Remove Dealt Price
        columnsToShowForNPVorRecoupon.push(
          ColId.feeAdjustedQuote, // = Dealt Price 14581
          ColId.dealer,
          ColId.dealtFeeOutright, // Cash (Fee Inc.)
          ColId.couponOutright,
        );
      }
      // else {
      //   columnsToHideForNPVorRecoupon.push(
      //     ColId.executionFeeOutright, // Fee
      //     ColId.feeAdjustedQuote,
      //   );
      // }
    } else if (unwind == false && containsNPV) {
      // 14283 - NO additional ER Cols
      if (!statusFormExecutedToAffirmedRange.includes(status)) {
        // 14287
      } else if (statusAtExecutedRange.includes(status)) {
        // 14291
      } else if (statusAtAffirmedRange.includes(status)) {
      }
    }

    // Recoupon
    // Can also handle OUTRIGHTS inside this.
    if (unwind == false && containsRecoupon) {
      if (statusAtExecutedRange.includes(status)) {
        // 14251 - No addition to ER - Hide Dealt Price.
        // columnsToHideForNPVorRecoupon.push(ColId.feeAdjustedQuote);
      } else if (statusAtAffirmedRange.includes(status)) {
        // 14256  - No changes to block.
      }
      if (strategyType == StrategyType.Outright) {
        //14585 adding for Recoupons
        if (
          statusAtExecutedRange.includes(status) ||
          status == ExecutionRequestStatus.Awarded // #14876, #14877, #14878, #14879, #14880, #14881 - Moving ReCoupon showing on Dealer and Cash cells forward to Awarded.
        ) {
          columnsToShowForNPVorRecoupon.push(
            ColId.dealer,
            ColId.cash,
            ColId.couponOutright,
          );
          if (statusAtExecutedRange.includes(status)) {
            columnsToShowForNPVorRecoupon.push(ColId.executionFeeOutright);
            columnsToShowForNPVorRecoupon.push(ColId.feeAdjustedQuote);
          }
        } else if (statusAtAffirmedRange.includes(status)) {
          // 14590 14591
          columnsToShowForNPVorRecoupon.push(
            ColId.dealer,
            ColId.dealtFeeOutright, //(Cash)
            ColId.couponOutright,
          );
        }
      }
    }

    if (
      structureType == StructureTypeCode.SYNREPO ||
      structureType == StructureTypeCode.SYNREVREP
    ) {
      if (status == ExecutionRequestStatus.Awarded) {
        const colDefsForSYNRepoAwarded = [
          ColId.bidRepo,
          ColId.askRepo,
          ColId.midRepo,
          ColId.quotedSize,
          // LQC-275: % Quoted column disabled on RP ER until RP-side behaviour is finalised
          // ...(tradeHasReducedQuotedSize(fullTradeData) ? [ColId.percentQuoted] : []),
          // ColId.validUntil here and below is QUOTE expiry, a later phase. It has no
          // base colDef so renders nothing. See docs/features/TRADE_EXPIRY.md
          ColId.validUntil,
          // Surfaced only when the OP advised the RP of the value (per-trade).
          ...coverColIdsWhenAdvised(fullTradeData),
        ];
        colDefsToReturn = returnColDefsAsNewArray(
          executionRequestColDefsRP,
          colDefsForSYNRepoAwarded,
          columnsToShowForNPVorRecoupon,
          columnsToHideForNPVorRecoupon,
        );
      } else if (
        statusAtExecutedRange.includes(status) ||
        statusAtAffirmedRange.includes(status)
      ) {
        const colDefsForSynRepoInExecutedRange = [
          ColId.bidRepo,
          ColId.askRepo,
          ColId.midRepo,
          ColId.feeAdjustedQuote,
          ColId.quotedSize,
          // LQC-275: % Quoted column disabled on RP ER until RP-side behaviour is finalised
          // ...(tradeHasReducedQuotedSize(fullTradeData) ? [ColId.percentQuoted] : []),
          ColId.validUntil,
          // Surfaced only when the OP advised the RP of the value (per-trade).
          ...coverColIdsWhenAdvised(fullTradeData),
        ];
        colDefsToReturn = returnColDefsAsNewArray(
          executionRequestColDefsRP,
          colDefsForSynRepoInExecutedRange,
          columnsToShowForNPVorRecoupon,
          columnsToHideForNPVorRecoupon,
        );
      } else {
        // All other Syn Repo
        const colDefSynRepoDefaultRPER = [
          ColId.bidRepo,
          ColId.askRepo,
          ColId.midRepo,
          ColId.quotedSize,
          // LQC-275: % Quoted column disabled on RP ER until RP-side behaviour is finalised
          // ...(tradeHasReducedQuotedSize(fullTradeData) ? [ColId.percentQuoted] : []),
          ColId.validUntil,
          // Surfaced only when the OP advised the RP of the value (per-trade).
          ...coverColIdsWhenAdvised(fullTradeData),
        ];
        colDefsToReturn = returnColDefsAsNewArray(
          executionRequestColDefsRP,
          colDefSynRepoDefaultRPER,
          columnsToShowForNPVorRecoupon,
          columnsToHideForNPVorRecoupon,
        );
      }
      return applyColumnWidthMultiplier(colDefsToReturn, columnWidthMultiplier);
    }

    // IN CASE OF OUTRIGHT, NO DROPDOWN TO LEG, AND BRING UP SOME COLUMNS FROM LEG LEVEL
    if (strategyType == StrategyType.Outright) {
      const tempColDefs = [];

      if (
        !fullTradeData ||
        !fullTradeData.legs ||
        fullTradeData.legs.length == 0
      ) {
        return [];
      }

      const securityType = fullTradeData.legs[0].securityType; // for outrights (only one block?)
      const instrumentType = fullTradeData.legs[0].instrumentType;

      if (
        securityType == SecurityTypeCode.SWO ||
        securityType == SecurityTypeCode.BNDO
      ) {
        tempColDefs.push(ColId.strikeOutright);
      }
      if (securityType == SecurityTypeCode.ZCS) {
        tempColDefs.push(ColId.cpiLeg1Outright);
        tempColDefs.push(ColId.cpiIndexOutright);
      }
      // if (securityType == SecurityTypeCode.ZCS) {
      //   tempColDefs.push(
      //     ColId.bidOutright,
      //     ColId.askOutright,
      //     ColId.midOutright,
      //     ColId.quotedSizeOutright,
      //     ...bookCols,
      //     ColId.securityOutright,
      //     ColId.validUntil,
      //     ...dealtPriceCols,
      //   );
      // }
      // else
      if (
        securityType == SecurityTypeCode.XCS ||
        securityType == SecurityTypeCode.XBS
      ) {
        tempColDefs.push(
          // ColId.bidOutright,
          // ColId.askOutright,
          // ColId.midOutright,
          // ColId.quotedSizeOutright,
          // ...bookCols,
          // ColId.securityOutright,
          // ColId.validUntil,
          ColId.notionalLeg2Outright,
          // ...dealtPriceCols,
        );
      }

      tempColDefs.push(
        ColId.bidOutright,
        ColId.askOutright,
        ColId.midOutright,
        ColId.direction, // was outright.
        ColId.notionalLeg1,
        ...bookCols,
        ColId.securityOutright,
        ...(tradeHasBondOrBondOption(fullTradeData)
          ? [ColId.isinOutright]
          : []),
        ColId.settlementDateOutright,
        ColId.quotedSizeOutright,
        // LQC-275: % Quoted column disabled on RP ER until RP-side behaviour is finalised
        // ...(tradeHasReducedQuotedSize(fullTradeData) ? [ColId.percentQuotedOutright] : []),
        ColId.validUntil,
        // Surfaced only when the OP advised the RP of the value (per-trade).
        ...coverColIdsWhenAdvised(fullTradeData),
        ...dealtPriceCols,
      );

      if (instrumentType == InstrumentTypeCode.OPT) {
        tempColDefs.push(ColId.expiryDate);
      }
      if (instrumentType == InstrumentTypeCode.FRN) {
        tempColDefs.push(
          ColId.referenceIndexOutright,
          ColId.quotedMarginOutright,
          ColId.currentCouponOutright,
        );
      }
      colDefsToReturn = returnColDefsAsNewArray(
        executionRequestColDefsRP,
        tempColDefs,
        columnsToShowForNPVorRecoupon,
        columnsToHideForNPVorRecoupon,
      );
    } else {
      const defaultColDefsForStructures = [
        ColId.bid,
        ColId.ask,
        ColId.mid,
        ColId.quotedSize,
        // LQC-275: % Quoted column disabled on RP ER until RP-side behaviour is finalised
        // ...(tradeHasReducedQuotedSize(fullTradeData) ? [ColId.percentQuoted] : []),
        ColId.validUntil,
        // Surfaced only when the OP advised the RP of the value (per-trade).
        ...coverColIdsWhenAdvised(fullTradeData),
        ...dealtPriceCols,
      ];

      colDefsToReturn = returnColDefsAsNewArray(
        executionRequestColDefsRP,
        defaultColDefsForStructures,
        columnsToShowForNPVorRecoupon,
        columnsToHideForNPVorRecoupon,
      );
    }
  } else if (type == "Order") {
    // let commonColDefsORLevelERForOrder = [
    //   ColId.provider,
    //   ColId.status,
    //   ColId.lastAction,
    // ];

    const colsForOrderRPErLevel = [
      // ColId.expand,
      // ...commonColDefsORLevelERForOrder, // NOT RFQ
      ColId.level,
      ColId.filledPercent,
      ...dealtPriceCols,
    ];
    colDefsToReturn = returnColDefsAsNewArray(
      executionRequestColDefsRP,
      colsForOrderRPErLevel,
      columnsToShowForNPVorRecoupon, //TODO: Con NPV or Recoupon swaps be orders?
      columnsToHideForNPVorRecoupon, //TODO: Con NPV or Recoupon swaps be orders?
    );
  }

  return applyColumnWidthMultiplier(colDefsToReturn, columnWidthMultiplier);
}
